import { Link } from 'react-router-dom';
import styles from './OrderCardV2.module.css';
import cakeService from '../../services/cakeorders.service';
import breadService from '../../services/breadorders.service';
import { ClockIcon, CheckIcon, ArrowLeftIcon } from './Icons';
import { Rellenos } from '../../models/CakeParams';

// Shared audio context - reuse across clicks to avoid creating many contexts
let sharedAudioCtx = null;
function getSharedAudioContext() {
    if (!sharedAudioCtx) {
        sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (sharedAudioCtx.state === 'suspended') {
        sharedAudioCtx.resume();
    }
    return sharedAudioCtx;
}

// Play success sound when marking ready
function playReadySound() {
    try {
        const ctx = getSharedAudioContext();
        const now = ctx.currentTime;

        // Rising two-tone success chime (G5 -> C6)
        [784, 1047].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0, now + i * 0.12);
            gain.gain.linearRampToValueAtTime(0.3, now + i * 0.12 + 0.03);
            gain.gain.linearRampToValueAtTime(0, now + i * 0.12 + 0.25);
            osc.start(now + i * 0.12);
            osc.stop(now + i * 0.12 + 0.25);
        });
    } catch (e) {
        // Audio not available
    }
}

// Get filling color
function getFillingColor(filling) {
    if (!filling) return null;
    const key = filling.toLowerCase().trim();
    return Rellenos[key]?.color || '#CCCCCC';
}

// Cake Order Card
export function CakeCardV2({ order }) {
    const isReady = order.ready;
    const fill1 = order.filling1?.trim();
    const fill2 = order.filling2?.trim();
    const displayImageUrl = order.createdImageUrl || order.imageUrl;
    const hasImage = !!displayImageUrl;
    const imageReady = order.imageReady;

    async function handleToggle() {
        if (!isReady) playReadySound();
        await cakeService.toggleReadiness(order.id, order.ready);
    }

    return (
        <div className={`${styles['order-card-v2']} ${isReady ? styles['ready'] : styles['pending']}`}>
            <div className={styles['card-header-v2']}>
                <span className={`${styles['status-badge-v2']} ${isReady ? styles['ready'] : styles['pending']}`}>
                    {isReady ? 'LISTO' : 'PENDIENTE'}
                </span>
                <span className={styles['pickup-time-v2']}>
                    <ClockIcon size={18} />
                    {order.time}
                </span>
            </div>

            <h3 className={styles['customer-name-v2']}>
                {order.firstName} {order.lastName}
            </h3>

            <div className={styles['cake-specs']}>
                <span className={styles['spec-item']}>{order.cakeSize}</span>
                <span className={styles['spec-divider']}>•</span>
                <span className={styles['spec-item']}>
                    <span className={styles['spec-label']}>COLOR:</span> {order.color}
                </span>
                {fill1 && (
                    <>
                        <span className={styles['spec-divider']}>•</span>
                        <span className={styles['spec-item']}>
                            <span className={styles['filling-dot']} style={{ backgroundColor: getFillingColor(fill1) }} />
                            {fill1}
                        </span>
                    </>
                )}
                {fill2 && (
                    <>
                        <span className={styles['spec-divider']}>•</span>
                        <span className={styles['spec-item']}>
                            <span className={styles['filling-dot']} style={{ backgroundColor: getFillingColor(fill2) }} />
                            {fill2}
                        </span>
                    </>
                )}
            </div>

            {order.image && (
                <div className={styles['image-info-v2']}>
                    <div className={`${styles['topper-status']} ${
                        !order.imageNeeded ? styles['no-needed'] :
                        imageReady ? styles['ready'] : styles['pending']
                    }`}>
                        {!order.imageNeeded ? 'NO NECESITA IMAGEN' :
                         imageReady ? 'IMAGEN LISTA' : 'ESPERANDO IMAGEN'}
                    </div>
                    <div className={styles['image-description-v2']}>
                        <span className={styles['spec-label']}>IMAGEN:</span> {order.image}
                    </div>
                    {order.imageNeeded && (
                        <Link to={`/topper/${order.id}`} className={styles['design-topper-link']}>
                            🎨 Diseñar topper
                        </Link>
                    )}
                </div>
            )}

            {hasImage && (
                <img
                    src={displayImageUrl}
                    alt="Referencia"
                    className={styles['reference-image-v2']}
                />
            )}

            {order.message && (
                <p className={styles['message-v2']}>"{order.message}"</p>
            )}

            {order.comments && order.comments.trim() && (
                <p className={styles['comments-v2']}>{order.comments}</p>
            )}

            <button
                className={`${styles['toggle-button-v2']} ${isReady ? styles['ready'] : styles['pending']}`}
                onClick={handleToggle}
            >
                {isReady ? (
                    <><ArrowLeftIcon size={20} /> MOVER A PENDIENTES</>
                ) : (
                    <><CheckIcon size={20} /> MARCAR COMO LISTO</>
                )}
            </button>
        </div>
    );
}

// Bread Order Card
export function BreadCardV2({ order }) {
    const isPending = order.status === 'pending';
    const isCompleted = order.status === 'completed';

    function handleToggle() {
        if (order.status === 'pending') {
            playReadySound();
            breadService.updateOrderStatus(order.id, 'baked');
        } else if (order.status === 'baked') {
            breadService.updateOrderStatus(order.id, 'pending');
        }
    }

    return (
        <div className={`${styles['order-card-v2']} ${isPending ? styles['pending'] : styles['ready']} ${isCompleted ? styles['completed'] : ''}`}>
            <div className={styles['card-header-v2']}>
                <span className={`${styles['status-badge-v2']} ${isPending ? styles['pending'] : styles['ready']}`}>
                    {isCompleted ? 'EMPACADO' : isPending ? 'PENDIENTE' : 'LISTO'}
                </span>
                <span className={styles['pickup-time-v2']}>
                    <ClockIcon size={18} />
                    {order.time}
                </span>
            </div>

            <h3 className={styles['customer-name-v2']}>
                {order.firstName} {order.lastName}
            </h3>

            <div className={styles['bread-list-v2']}>
                {order.bread && order.bread.map((item, index) => (
                    <div key={index} className={styles['bread-item-v2']}>
                        <span className={styles['bread-quantity']}>{item.quantity}x</span>
                        <span className={styles['bread-name']}>{item.bread}</span>
                    </div>
                ))}
            </div>

            {order.comments && order.comments.trim() && (
                <p className={styles['comments-v2']}>{order.comments}</p>
            )}

            {!isCompleted && (
                <button
                    className={`${styles['toggle-button-v2']} ${isPending ? styles['pending'] : styles['ready']}`}
                    onClick={handleToggle}
                >
                    {isPending ? (
                        <><CheckIcon size={20} /> MARCAR COMO LISTO</>
                    ) : (
                        <><ArrowLeftIcon size={20} /> MOVER A PENDIENTES</>
                    )}
                </button>
            )}
        </div>
    );
}
